import { useFormContext, Controller } from "react-hook-form";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Consultation, BodyAnalysis } from "@/types/consultation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo } from "react";
import { differenceInYears, parseISO } from "date-fns";
import { useGetBodyAnalysisQuery } from "@/services/consultationApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { HistoryFilter } from "../history-filter";
import { useState } from "react";
import { HistoryConsultation } from "@/types/consultation";
export const sections = [
  { title: "Cardio Test", key: "cardio" },
  { title: "Endurance Test", key: "endurance" },
  { title: "Flexibility Test", key: "flexibility" },
] as const;

const ratingOptions = ["Excellent", "Good", "Fair", "Poor"];

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const BodyAnalysisStep = ({ action, open }: FormStepProps) => {
  const { register, control, watch, setValue, getValues } = useFormContext<Consultation>();
  const memberDob = watch("member_dob");
  const consultationId = watch("id");
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  // Fetch existing analysis if in edit mode
  const { data: analysisData } = useGetBodyAnalysisQuery(
    { consultation_id: Number(consultationId), org_id: orgId },
    { skip: action !== "edit" || !consultationId || !open }
  );

  // Populate form when data is fetched
  useEffect(() => {
    if (analysisData?.data && Array.isArray(analysisData.data)) {
      analysisData.data.forEach((item) => {
        const key = item.body_test_type;
        if (item.body_test_rank) {
          // Standardize to Title Case for UI matching (e.g., "good" -> "Good")
          const formattedRank = item.body_test_rank.charAt(0).toUpperCase() + 
                               item.body_test_rank.slice(1).toLowerCase();
          setValue(`body_analysis.${key}.rank`, formattedRank, { shouldDirty: false });
        }

        if (item.body_test_rate !== undefined) {
          setValue(`body_analysis.${key}.rate`, item.body_test_rate, { shouldDirty: false });
        }
        
        // Populate stored age if available
        if (item.age) {
          setValue(`body_analysis.${key}.age`, item.age, { shouldDirty: false });
        }
      });
    }
  }, [analysisData, setValue]);

  // Calculate age from DOB
  const calculatedAge = useMemo(() => {
    if (!memberDob) return null;
    try {
      const birthDate = parseISO(memberDob);
      const age = differenceInYears(new Date(), birthDate);
      return isNaN(age) ? null : age;
    } catch (e) {
      return null;
    }
  }, [memberDob]);

  // Prefill age fields if they are empty or zero
  useEffect(() => {
    if (open && calculatedAge !== null) {
      const currentValues = getValues();
      sections.forEach((section) => {
        const currentAge = currentValues.body_analysis?.[section.key]?.age;
        if (
          currentAge === undefined ||
          currentAge === null ||
          currentAge === 0 ||
          (typeof currentAge === "number" && isNaN(currentAge))
        ) {
          setValue(`body_analysis.${section.key}.age`, calculatedAge, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      });
    }
  }, [open, calculatedAge, setValue, getValues]);

  const [historyData, setHistoryData] = useState<HistoryConsultation | null>(null);
  const memberId = watch("member_id");

  return (
    <div className="space-y-4">
      <HistoryFilter 
        memberId={memberId!} 
        onDataFound={setHistoryData} 
      />

      {historyData && (
        <Card className="p-4 border-[#e2e8f0]">
          <div className="flex items-center justify-between mb-3">
             <p className="font-semibold text-sm text-[#1e293b]">Previous Analysis Results</p>
             <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 uppercase">
                Historical View
             </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             {historyData.body_test_analysis.map((test) => (
                <div key={test.body_test_type} className="bg-[#f8fafc] border border-[#f1f5f9] p-3 rounded-lg">
                   <p className="text-xs font-bold text-[#64748b] uppercase mb-2 border-b border-[#e2e8f0] pb-1">
                      {test.body_test_type}
                   </p>
                   <div className="flex justify-between items-center text-xs">
                      <span className="text-[#64748b]">Rate:</span>
                      <span className="font-semibold text-[#1e293b]">{test.body_test_rate || "N/A"}</span>
                   </div>
                   <div className="flex justify-between items-center text-xs mt-1">
                      <span className="text-[#64748b]">Rank:</span>
                      <span className="font-semibold text-[#10b981]">{test.body_test_rank || "N/A"}</span>
                   </div>
                </div>
             ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        <div className="mb-4">
          <p className="font-medium text-sm">Body Analysis</p>
          <p className="text-xs text-gray-500 mt-1">
            Complete the following tests to analyze the member's physical condition.
          </p>
        </div>
        <Separator className="h-[1px] mb-2" />

        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.key} className="space-y-4">
              <h3 className="text-sm font-semibold text-[#1e293b] border-b-2 border-[#6be27f] w-fit pb-1">
                {section.title}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2 items-end">
                <div className="w-full">
                  <FloatingLabelInput
                    label="Age"
                    type="number"
                    disabled={true}
                    {...register(`body_analysis.${section.key}.age`, { valueAsNumber: true })}
                  />
                </div>

                <div className="w-full">
                  <FloatingLabelInput
                    label="Rate"
                    type="number"
                    step="1"
                    inputMode="numeric"
                    pattern="\d*"
                    {...register(`body_analysis.${section.key}.rate`, { 
                      valueAsNumber: true
                    })}
                  />
                </div>
                
                <div className="w-full">
                  <Controller
                    control={control}
                    name={`body_analysis.${section.key}.rank`}
                    render={({ field }) => (
                      <div className="space-y-1 flex flex-col justify-end h-full">
                        <label className="text-[12px] text-[#64748b] font-medium px-0.5 leading-none">
                          Rank
                        </label>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger className="bg-white border-gray-200 focus:ring-0 rounded-md shadow-sm">
                            <SelectValue placeholder="Select Rank" />
                          </SelectTrigger>
                          <SelectContent>
                            {ratingOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default BodyAnalysisStep;
